import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  GraduationCap, FileText, CheckCircle2, Upload, X as XIcon, Loader2,
  Save, CalendarDays, Lock, ClipboardCheck, Award, ChevronDown,
} from "lucide-react";
import { myApi, PHOTO_URL } from "@/my/api";

const DOC_TYPES = [
  { key: "ccp", label: "CCP", hint: "Certificado de Competências Pedagógicas" },
  { key: "seguro", label: "Seguro de formador", hint: "Apólice em vigor" },
  { key: "ficha_formador", label: "Ficha de formador", hint: "Ficha identificativa assinada" },
];

const CARD = "bg-white rounded-[20px] border border-[#E6E1D6] p-6 lg:p-8";
const LABEL = "block text-xs font-medium text-[#6B7280] mb-1";
const INPUT = "w-full rounded-lg border border-[#E6E1D6] px-3 py-2 text-sm text-[#0F1829] focus:outline-none focus:border-[#0F1829]";

const IBAN_LENGTHS = { AD:24,AT:20,BE:16,CH:21,CY:28,CZ:24,DE:22,DK:18,EE:20,ES:24,FI:18,FR:27,GB:22,GR:27,HR:21,HU:28,IE:22,IS:26,IT:27,LI:21,LT:20,LU:20,LV:21,MC:27,MT:31,NL:18,NO:15,PL:28,PT:25,RO:24,SE:24,SI:19,SK:24,SM:27 };
function normalizeIban(v) { return (v || "").replace(/\s+/g, "").toUpperCase(); }
function isValidIban(v) {
  const s = normalizeIban(v);
  if (s.length < 15 || s.length > 34) return false;
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]+$/.test(s)) return false;
  const exp = IBAN_LENGTHS[s.slice(0, 2)];
  if (exp && s.length !== exp) return false;
  const rearranged = s.slice(4) + s.slice(0, 4);
  const digits = rearranged.replace(/[A-Z]/g, (c) => (c.charCodeAt(0) - 55).toString());
  // mod-97 em pedaços (evita overflow de inteiros grandes)
  let rem = 0;
  for (let i = 0; i < digits.length; i += 7) {
    rem = parseInt(String(rem) + digits.substr(i, 7), 10) % 97;
  }
  return rem === 1;
}

export default function MyAreaFormador() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ habilitacoes: "", certificacoes: "", experiencia: "", linkedin: "", iban: "" });
  const [saving, setSaving] = useState(false);
  const [trainerOpen, setTrainerOpen] = useState(true);
  const [imgOk, setImgOk] = useState(true);
  const [ibanError, setIbanError] = useState("");
  const upd = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const load = () => {
    myApi.formadorInfo()
      .then((d) => {
        setData(d);
        const p = d.perfil || {};
        setForm({
          habilitacoes: p.habilitacoes || "", certificacoes: p.certificacoes || "",
          experiencia: p.experiencia || "", linkedin: p.linkedin || "", iban: p.iban || "",
        });
      })
      .catch(() => setData({ is_formador: false }))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const save = async () => {
    if (form.iban && !isValidIban(form.iban)) {
      setIbanError("IBAN inválido. Verifique o número (ex.: PT50 seguido de 21 dígitos).");
      toast.error("IBAN inválido.");
      return;
    }
    setIbanError("");
    const payload = { ...form, iban: form.iban ? normalizeIban(form.iban) : "" };
    setSaving(true);
    try {
      await myApi.formadorPerfilUpdate(payload);
      if (payload.iban) upd("iban", payload.iban);
      toast.success("Dados de formador atualizados.");
    } catch (e) { toast.error(e.message || "Não foi possível guardar."); }
    finally { setSaving(false); }
  };

  if (loading) {
    return <div className="flex items-center gap-2 text-[#978D78] p-8"><Loader2 className="w-5 h-5 animate-spin" /> A carregar…</div>;
  }
  if (!data?.is_formador) {
    return (
      <div className={CARD} data-testid="my-area-formador-empty">
        <p className="text-sm text-[#6B7280]">Esta área é reservada a formadores da IHTP.</p>
      </div>
    );
  }

  const perfil = data.perfil || {};
  const cursos = data.cursos || [];
  const initials = (perfil.nome || "?").split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();

  return (
    <div className="space-y-6" data-testid="my-area-formador">
      {/* Cabeçalho + identificação */}
      <div className={CARD}>
        <div className="flex items-center gap-4 flex-wrap">
          {imgOk ? (
            <img src={PHOTO_URL} alt={perfil.nome} onError={() => setImgOk(false)}
              className="w-16 h-16 rounded-full object-cover ring-1 ring-[#E6E1D6]" data-testid="my-formador-foto" />
          ) : (
            <div className="w-16 h-16 rounded-full grid place-items-center bg-[#4A5E2E] text-white text-lg font-semibold">{initials}</div>
          )}
          <div className="min-w-0">
            <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#6F4F1D] bg-[#F5F2EB] border border-[#E6E1D6] px-2 py-0.5 rounded-full mb-1">
              <Award size={11} /> Formador IHTP
            </div>
            <h1 className="text-2xl text-[#0F1829] leading-tight" style={{ fontFamily: "'Fraunces', serif", fontWeight: 500 }}>
              {perfil.nome || "Formador"}
            </h1>
            <div className="text-xs text-[#978D78] mt-0.5">
              {perfil.email || "—"}{perfil.telefone ? ` · ${perfil.telefone}` : ""}{perfil.nif ? ` · NIF ${perfil.nif}` : ""}
            </div>
            <div className="text-[11px] text-[#B0A88F] mt-0.5">A foto vem das suas Informações Pessoais.</div>
          </div>
        </div>
      </div>

      {/* Dados profissionais editáveis */}
      <div className={CARD}>
        <h2 className="text-xl text-[#0F1829] mb-1" style={{ fontFamily: "'Fraunces', serif", fontWeight: 500 }}>Dados profissionais</h2>
        <p className="text-xs text-[#978D78] mb-5">Mantenha o seu perfil de formador atualizado.</p>
        <div className="space-y-4">
          <div>
            <label className={LABEL}>Habilitações</label>
            <textarea className={INPUT} rows={3} value={form.habilitacoes} onChange={(e) => upd("habilitacoes", e.target.value)}
              placeholder="Formação académica, graus, áreas…" data-testid="formador-habilitacoes" />
          </div>
          <div>
            <label className={LABEL}>Certificações</label>
            <textarea className={INPUT} rows={3} value={form.certificacoes} onChange={(e) => upd("certificacoes", e.target.value)}
              placeholder="Certificações profissionais relevantes…" data-testid="formador-certificacoes" />
          </div>
          <div>
            <label className={LABEL}>Experiência profissional</label>
            <textarea className={INPUT} rows={4} value={form.experiencia} onChange={(e) => upd("experiencia", e.target.value)}
              placeholder="Percurso, áreas de especialidade, anos de experiência…" data-testid="formador-experiencia" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={LABEL}>LinkedIn</label>
              <input className={INPUT} value={form.linkedin} onChange={(e) => upd("linkedin", e.target.value)}
                placeholder="https://linkedin.com/in/…" data-testid="formador-linkedin" />
            </div>
            <div>
              <label className={LABEL}>IBAN</label>
              <input className={`${INPUT} ${ibanError ? "border-red-400" : ""}`} value={form.iban}
                onChange={(e) => { upd("iban", e.target.value); if (ibanError) setIbanError(""); }}
                onBlur={(e) => { if (e.target.value) upd("iban", normalizeIban(e.target.value)); }}
                placeholder="PT50 …" data-testid="formador-iban" />
              {ibanError && <p className="text-xs text-red-600 mt-1" data-testid="formador-iban-error">{ibanError}</p>}
            </div>
          </div>
          <div className="flex justify-end">
            <button onClick={save} disabled={saving} data-testid="formador-perfil-save"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#0F1829] text-white text-sm font-medium hover:bg-[#1a2740] disabled:opacity-50">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Guardar alterações
            </button>
          </div>
        </div>
      </div>

      {/* Documentos */}
      <FormadorDocs docs={data.docs || {}} onChange={(nd) => setData({ ...data, docs: { ...(data.docs || {}), ...nd } })} />

      {/* Cursos como formador */}
      {cursos.length > 0 && (
        <div className={CARD} data-testid="my-trainer-courses">
          <button type="button" onClick={() => setTrainerOpen((v) => !v)} data-testid="my-trainer-courses-toggle"
            aria-expanded={trainerOpen} className="w-full flex items-center justify-between gap-4 flex-wrap text-left">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#F5F2EB] text-[#6F4F1D] grid place-items-center"><GraduationCap size={18} strokeWidth={1.7} /></div>
              <div>
                <h2 className="text-xl text-[#0F1829] leading-tight" style={{ fontFamily: "'Fraunces', serif", fontWeight: 500 }}>Os seus cursos como Formador</h2>
                <div className="text-xs text-[#978D78] mt-0.5">{cursos.length} curso{cursos.length === 1 ? "" : "s"} associado{cursos.length === 1 ? "" : "s"}</div>
              </div>
            </div>
            <ChevronDown size={20} strokeWidth={1.7} className={`text-[#978D78] shrink-0 transition-transform ${trainerOpen ? "rotate-180" : ""}`} />
          </button>
          {trainerOpen && (
            <div className="mt-5 divide-y divide-[#F5F2EB]">
              {cursos.map((c) => (
                <div key={c.id_curso} data-testid={`my-trainer-course-${c.id_curso}`} className="flex items-center justify-between py-3 gap-4 flex-wrap sm:flex-nowrap">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-[#0F1829] truncate flex items-center gap-2">
                      <span className="truncate">{c.nome}</span>
                      {c.edicao && <span className="text-xs text-[#978D78] shrink-0">· Edição {c.edicao}</span>}
                      {c.encerrado && (
                        <span data-testid={`my-trainer-course-${c.id_curso}-encerrado`}
                          className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] uppercase tracking-wider bg-[#F5F2EB] text-[#6F4F1D] border border-[#E6E1D6]" title="Curso encerrado">
                          <Lock size={9} /> Encerrado
                        </span>
                      )}
                    </div>
                    {(c.data_inicio || c.data_fim) && (
                      <div className="text-xs text-[#6B7280] mt-0.5 inline-flex items-center gap-1.5">
                        <CalendarDays size={11} className="text-[#978D78]" />
                        {c.data_inicio ? c.data_inicio.slice(0, 10) : "—"}{c.data_fim ? ` → ${c.data_fim.slice(0, 10)}` : ""}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <Link to={`/my/formador/curso/${c.id_curso}/avaliacao`} data-testid={`my-trainer-course-${c.id_curso}-avaliar`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs bg-[#F5F2EB] text-[#0F1829] hover:bg-[#E6E1D6] transition"
                      title={c.encerrado ? "Consultar avaliação (Mod. 16)" : "Avaliar curso (Mod. 16)"}>
                      <ClipboardCheck size={12} />{c.encerrado ? "Consultar Mod. 16" : "Avaliar Mod. 16"}
                    </Link>
                    <span className="text-xs text-[#978D78]">#{c.id_curso}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function FormadorDocs({ docs, onChange }) {
  const [busy, setBusy] = useState(null);
  const upload = async (tipo, file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) { toast.error("Apenas PDF é aceite."); return; }
    setBusy(tipo);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`/api/my/formador/docs/${tipo}/upload`, { method: "POST", credentials: "include", body: fd });
      if (!res.ok) throw new Error(await res.text());
      const j = await res.json();
      onChange({ [tipo]: { url: j.url, uploaded_em: new Date().toISOString() } });
      toast.success("Documento carregado.");
    } catch (e) { toast.error(`Upload falhou: ${e.message || e}`); }
    finally { setBusy(null); }
  };
  const remove = async (tipo) => {
    if (!window.confirm("Remover documento?")) return;
    setBusy(tipo);
    try {
      const res = await fetch(`/api/my/formador/docs/${tipo}`, { method: "DELETE", credentials: "include" });
      if (!res.ok) throw new Error(await res.text());
      onChange({ [tipo]: null });
    } catch (e) { toast.error(`Erro: ${e.message || e}`); }
    finally { setBusy(null); }
  };

  return (
    <div data-testid="my-formador-docs" className={CARD}>
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-full bg-[#F5F2EB] text-[#6F4F1D] grid place-items-center"><FileText size={18} strokeWidth={1.7} /></div>
        <div>
          <h2 className="text-xl text-[#0F1829] leading-tight" style={{ fontFamily: "'Fraunces', serif", fontWeight: 500 }}>Documentos de Formador</h2>
          <div className="text-xs text-[#978D78] mt-0.5">CCP · Seguro · Ficha — mantenha os documentos actualizados.</div>
        </div>
      </div>
      <div className="space-y-2">
        {DOC_TYPES.map(({ key, label, hint }) => {
          const doc = docs?.[key];
          const isBusy = busy === key;
          return (
            <div key={key} data-testid={`my-formador-doc-${key}`} className="flex items-center gap-3 p-3 border border-[#F0EBDF] rounded-lg">
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-[#0F1829]">{label}</div>
                <div className="text-[11px] text-[#6B7280]">
                  {doc ? (
                    <span className="inline-flex items-center gap-1">
                      <CheckCircle2 size={11} className="text-[#1F7A3B]" /> Upload:{" "}
                      <span className="font-semibold text-[#1F7A3B]" data-testid={`my-formador-doc-date-${key}`}>
                        {new Date(doc.uploaded_em).toLocaleDateString("pt-PT", { day: "2-digit", month: "long", year: "numeric" })}
                      </span>
                    </span>
                  ) : hint}
                </div>
              </div>
              {doc ? (
                <>
                  <a href={doc.url} target="_blank" rel="noopener noreferrer" data-testid={`my-formador-doc-link-${key}`}
                    className="text-xs px-3 py-1.5 rounded-full bg-[#E7F5EB] text-[#1F7A3B] hover:bg-[#1F7A3B] hover:text-white inline-flex items-center gap-1">
                    <CheckCircle2 size={12} /> Ver PDF
                  </a>
                  <button type="button" onClick={() => remove(key)} disabled={isBusy} data-testid={`my-formador-doc-remove-${key}`}
                    className="text-xs px-2 py-1.5 rounded-full text-red-600 hover:bg-red-50">
                    {isBusy ? <Loader2 size={12} className="animate-spin" /> : <XIcon size={12} />}
                  </button>
                </>
              ) : (
                <label data-testid={`my-formador-doc-upload-label-${key}`}
                  className="cursor-pointer text-xs px-3 py-1.5 rounded-full border border-[#0F1829] text-[#0F1829] hover:bg-[#0F1829] hover:text-white inline-flex items-center gap-1">
                  {isBusy ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
                  {isBusy ? "A carregar…" : "Upload PDF"}
                  <input type="file" accept="application/pdf" className="hidden" disabled={isBusy}
                    onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) upload(key, f); }}
                    data-testid={`my-formador-doc-upload-${key}`} />
                </label>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
